import { useState } from 'react';

import { DocumentUploadCard } from '../DocumentUploadCard';

interface LegalIdentityData {
  company_name: string;
  legal_rep_first_name: string;
  legal_rep_last_name: string;
  legal_rep_email: string;
  legal_rep_phone: string;
  legal_rep_is_ubo: boolean;
}

interface LegalIdentityStepProps {
  initialData?: Partial<LegalIdentityData>;
  uploadedDocs?: { id: string; documentType: string; originalFilename: string; fileSize: number }[];
  onSubmit: (data: LegalIdentityData) => void;
  onUploadDoc: (documentType: string, file: File) => void;
  onDeleteDoc?: (documentId: string) => void;
  isSubmitting?: boolean;
  uploadingDocType?: string | null;
}

const inputClass =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

export const LegalIdentityStep: React.FC<LegalIdentityStepProps> = ({
  initialData,
  uploadedDocs = [],
  onSubmit,
  onUploadDoc,
  onDeleteDoc,
  isSubmitting = false,
  uploadingDocType = null,
}) => {
  const [form, setForm] = useState<LegalIdentityData>({
    company_name: initialData?.company_name ?? '',
    legal_rep_first_name: initialData?.legal_rep_first_name ?? '',
    legal_rep_last_name: initialData?.legal_rep_last_name ?? '',
    legal_rep_email: initialData?.legal_rep_email ?? '',
    legal_rep_phone: initialData?.legal_rep_phone ?? '',
    legal_rep_is_ubo: initialData?.legal_rep_is_ubo ?? false,
  });

  const set = <K extends keyof LegalIdentityData>(key: K, value: LegalIdentityData[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const getDoc = (type: string) => uploadedDocs.find(d => d.documentType === type);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-gray-800 mb-4">Información de la empresa</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label htmlFor="companyName" className={labelClass}>
              Nombre legal de la empresa *
            </label>
            <input
              className={inputClass}
              value={form.company_name}
              onChange={e => set('company_name', e.target.value)}
              required
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-800 mb-4">Representante Legal</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="legalRepFirstName" className={labelClass}>
              Nombre(s) *
            </label>
            <input
              className={inputClass}
              value={form.legal_rep_first_name}
              onChange={e => set('legal_rep_first_name', e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="legalRepLastName" className={labelClass}>
              Apellido(s) *
            </label>
            <input
              className={inputClass}
              value={form.legal_rep_last_name}
              onChange={e => set('legal_rep_last_name', e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="legalRepEmail" className={labelClass}>
              Email *
            </label>
            <input
              className={inputClass}
              type="email"
              value={form.legal_rep_email}
              onChange={e => set('legal_rep_email', e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="legalRepPhone" className={labelClass}>
              Teléfono *
            </label>
            <input
              className={inputClass}
              type="tel"
              value={form.legal_rep_phone}
              onChange={e => set('legal_rep_phone', e.target.value)}
              required
            />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <input
            id="is-ubo"
            type="checkbox"
            checked={form.legal_rep_is_ubo}
            onChange={e => set('legal_rep_is_ubo', e.target.checked)}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded"
          />
          <label htmlFor="is-ubo" className="text-sm text-gray-700 cursor-pointer">
            El representante legal es también beneficiario final (propietario con ≥25% de
            participación)
          </label>
        </div>
      </div>

      {!form.legal_rep_is_ubo && (
        <div>
          <h3 className="text-base font-semibold text-gray-800 mb-2">
            Documentos del Representante Legal
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Requeridos cuando el representante legal no es beneficiario final.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <DocumentUploadCard
              label="Identificación oficial"
              documentType="identificacion_rl"
              uploadedFile={getDoc('identificacion_rl')}
              onUpload={onUploadDoc}
              onDelete={onDeleteDoc}
              uploadingDocType={uploadingDocType}
            />
            <DocumentUploadCard
              label="Constancia de Situación Fiscal"
              documentType="constancia_csf_rl"
              uploadedFile={getDoc('constancia_csf_rl')}
              onUpload={onUploadDoc}
              onDelete={onDeleteDoc}
              uploadingDocType={uploadingDocType}
            />
            <DocumentUploadCard
              label="Comprobante de domicilio"
              documentType="comprobante_domicilio_rl"
              uploadedFile={getDoc('comprobante_domicilio_rl')}
              onUpload={onUploadDoc}
              onDelete={onDeleteDoc}
              uploadingDocType={uploadingDocType}
            />
          </div>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
        >
          {isSubmitting ? 'Guardando...' : 'Siguiente'}
        </button>
      </div>
    </form>
  );
};
